import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { isEmpty } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useLobby from "@/hooks/use-lobby";

// Interface for props
interface WelcomeBoxProps {
  backgroundColor?: string;
  textColor?: string;
}

// Define the new chart colors
const COLORS = ["#644980", "#815fb5", "#9678d3", "#b49ee4", "#d1bce8"];

const BarChartComponent: React.FC<WelcomeBoxProps> = ({
  backgroundColor = "#fff", // Default background color
  textColor = "#333", // Default text color
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { gettenderStatus } = useLobby();
  const [tenderStatus, setTenderStatus] = useState<
    { status: string; total: number }[]
  >([]);
  const [loading, setLoading] = useState(true); // State to handle loading

  useEffect(() => {
    const getTotaltenderQ = async () => {
      try {
        const Total = await gettenderStatus();

        if (!isEmpty(Total.totals)) {
          // Ensure total is converted to a number
          const formattedData = Object.entries(Total.totals).map(
            ([status, total]) => ({
              status,
              total: total as number,
            })
          );
          setTenderStatus(formattedData);
        } else {
          // Default data when no response is returned
          setTenderStatus([
            { status: t("statistics.finished"), total: 100 },
            { status: t("statistics.unfinished"), total: 0 },
          ]);
        }
      } catch (error) {
        console.error("Error fetching tender status:", error);
        // Handle error case by setting default data
        setTenderStatus([
          { status: t("statistics.finished"), total: 100 },
          { status: t("statistics.unfinished"), total: 0 },
        ]);
      } finally {
        setLoading(false); // Ensure loading state is set to false
      }
    };

    getTotaltenderQ();
  }, []);

  return (
    <div
      className="py-2 px-2 md:py-4 md:px-5"
      style={{
        borderRadius: "8px",
        backgroundColor,
        color: textColor,
        textAlign: "center",
        height: "100%",
      }}
    >
      <h3
        style={{
          marginBottom: "20px",
          fontWeight: "bold",
          fontSize: "18px",
          color: textColor,
        }}
      >
        {t("statistics.chartTitle")}
      </h3>
      {loading ? (
        <div style={{ textAlign: "center", padding: "20px", fontSize: "18px" }}>
          {t("status.loading")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Tooltip
              formatter={(value: number, name: string) => [`${value}`, name]}
            />
            <Pie
              data={tenderStatus}
              dataKey="total" // Correct key for 'total'
              nameKey="status" // Correct key for 'status'
              cx="50%"
              cy="50%"
              outerRadius={100} // Outer radius for the doughnut
              innerRadius={70} // Inner radius to create the doughnut effect
              fill="#8884d8"
              label={({ name, value }) => `${name}: ${value}`} // Arabic label formatting
            >
              {tenderStatus.map((entry, index) => {
                return (
                  <Cell
                    key={`cell-${index}-value-${entry.total}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                );
              })}
            </Pie>
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ marginTop: "10px" }} // Adjust the margin as needed
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default BarChartComponent;
